import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WoundService } from '../../../../core/services/wound/wound.service';
import { PatientsService } from '../../../../core/services/patient/patients.service';
import {
  GetWoundByIdResponse,
  WoundAssessmentsDto,
  GetListWoundByPatientIdResponse,
  WoundStatus,
  WoundCategoryType,
} from 'app/core/models/wound/wound.model';
import { PatientDetailResponse } from 'app/core/models/patient/patient-detail.model';
import { BaseApiResponse } from 'app/core/models/api/api.model';
import { PatientOverviewComponent } from '../patient-detail/patient-overview/patient-overview.component';
import { ModelViewerComponent } from 'app/main/components/model-viewer/model-viewer.component';
import { SelectComponent } from 'app/shared/components/select/select.component';
import { WoundAssessmentDetailComponent } from 'app/shared/components/wound-assessment-detail/wound-assessment-detail.component';
import { DocumentManagerComponent } from 'app/shared/components/document-manager/document-manager.component';
import { CurrentUserService } from 'app/core/services/auth/current-user.service';
import { WoundAnalysisMetrics } from 'app/core/models/wound/wound.model';
import { WoundAnalysisMetricsComponent } from 'app/shared/components/wound-analysis-metrics/wound-analysis-metrics.component';

@Component({
  selector: 'app-wound-assessment',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    PatientOverviewComponent,
    ModelViewerComponent,
    SelectComponent,
    ReactiveFormsModule,
    WoundAssessmentDetailComponent,
    DocumentManagerComponent,
    WoundAnalysisMetricsComponent,
  ],
  templateUrl: './wound-assessment.component.html',
  styleUrls: ['./wound-assessment.component.scss'],
})
export class WoundAssessmentComponent implements OnInit, AfterViewInit, OnDestroy {
  // Route Parameters
  patientId: string = '';
  woundId: string = '';

  // Data
  patient: PatientDetailResponse | null = null;
  wound: GetWoundByIdResponse | null = null;
  wounds: GetListWoundByPatientIdResponse[] = [];
  scores: any = {};
  latestAssessment: WoundAssessmentsDto | null = null;

  // State
  isLoading = true;
  hasError = false;
  private dataLoaded = false;
  private modelsLoadDone = false;
  parsedMetrics: WoundAnalysisMetrics | null = null;

  models: {
    name: string;
    url: string;
    woundId: string;
    notes: any[];
  }[] = [];
  selectedModel1: string | null = null;
  selectedModel1Notes: any[] = [];

  canCreateFollowUp: boolean = false;

  // Tab management
  activeTab: 'model' | 'documents' | 'analysis' = 'model';
  canUploadDocuments: boolean = false;

  assessmentForm = new FormGroup({
    selectedModel1: new FormControl<string | null>(null),
  });

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private woundService: WoundService,
    private patientsService: PatientsService,
    private elementRef: ElementRef,
    private currentUserService: CurrentUserService,
    private http: HttpClient,
  ) {}

  ngOnInit(): void {
    this.patientId = this.route.snapshot.paramMap.get('patientId') || '';
    this.woundId = this.route.snapshot.paramMap.get('woundId') || '';
    this.checkUserPermissions();
    this.assessmentForm.get('selectedModel1')?.valueChanges.subscribe((url) => {
      this.selectedModel1 = url;

      if (url) {
        const model = this.models.find((m) => m.url === url);
        this.selectedModel1Notes = model?.notes || [];
      } else {
        this.selectedModel1Notes = [];
      }
    });

    if (this.patientId && this.woundId) {
      this.loadData();
      this.loadModels();
    } else {
      this.hasError = true;
      this.isLoading = false;
    }
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Load patient and wound data
  private loadData(): void {
    this.isLoading = true;

    this.patientsService.getPatientById(this.patientId).subscribe({
      next: (response: PatientDetailResponse) => {
        this.patient = response;
        this.loadWoundData();
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
      },
    });
  }

  private loadWoundData(): void {
    this.woundService.getWoundById(this.woundId).subscribe({
      next: (data: BaseApiResponse<GetWoundByIdResponse>) => {
        this.wound = data.data;
        this.updateScoresFromAssessments();
        this.parseWoundMetrics();
        this.loadWoundsList();
        this.dataLoaded = true;
        this.checkAllLoaded();
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
      },
    });
  }

  private checkAllLoaded(): void {
    if (this.dataLoaded && this.modelsLoadDone) {
      this.isLoading = false;
    }
  }

  private parseWoundMetrics(): void {
    if (this.wound?.metricsPath) {
      this.http
        .get<Record<string, unknown>>(this.wound.metricsPath)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => this.applyMetrics(data),
          error: () => { this.parsedMetrics = null; },
        });
    }
  }

  private applyMetrics(raw: Record<string, unknown>): void {
    this.parsedMetrics = {
      surfaceAreaMm2: (raw['surface_area_mm2'] ?? raw['surfaceAreaMm2'] ?? 0) as number,
      surfaceAreaCm2: (raw['surface_area_cm2'] ?? raw['surfaceAreaCm2'] ?? 0) as number,
      volumeMm3: (raw['volume_mm3'] ?? raw['volumeMm3'] ?? 0) as number,
      volumeCm3: (raw['volume_cm3'] ?? raw['volumeCm3'] ?? 0) as number,
      volumeIsApproximate: (raw['volume_is_approximate'] ?? raw['volumeIsApproximate'] ?? false) as boolean,
      isWatertight: (raw['is_watertight'] ?? raw['isWatertight'] ?? false) as boolean,
      boundingBoxMm: (raw['bounding_box_mm'] ?? raw['boundingBoxMm']) as number[] | undefined,
      centroidMm: (raw['centroid_mm'] ?? raw['centroidMm']) as number[] | undefined,
      boundaryLengthMm: (raw['boundary_length_mm'] ?? raw['boundaryLengthMm'] ?? 0) as number,
      numVertices: (raw['num_vertices'] ?? raw['numVertices'] ?? 0) as number,
      numFaces: (raw['num_faces'] ?? raw['numFaces'] ?? 0) as number,
    };
  }

  private loadWoundsList(): void {
    if (!this.patientId) return;

    this.woundService.getListWoundByPatientId(this.patientId).subscribe({
      next: (data: BaseApiResponse<GetListWoundByPatientIdResponse[]>) => {
        this.wounds = data.data;
        this.checkSeriesStatus();
      },
      error: () => {
        this.canCreateFollowUp = false;
      },
    });
  }

  private checkSeriesStatus(): void {
    if (!this.wound || !this.wounds.length) {
      this.canCreateFollowUp = false;
      return;
    }

    let parentWoundId = this.wound.parentWoundId;

    if (!parentWoundId) {
      parentWoundId = this.wound.id;
    }

    const parentWound = this.wounds.find((w) => w.id === parentWoundId);

    if (!parentWound) {
      this.canCreateFollowUp = false;
      return;
    }

    if (parentWound.woundStatus !== WoundStatus.TreatmentCompleted) {
      this.canCreateFollowUp = false;
      return;
    }

    if (parentWound.followUps && parentWound.followUps.length > 0) {
      const activeChild = parentWound.followUps.find(
        (child) => child.woundStatus !== WoundStatus.TreatmentCompleted,
      );
      if (activeChild) {
        this.canCreateFollowUp = false;
        return;
      }
    }

    this.canCreateFollowUp = true;
  }

  handleMarkerCreation(event: any) {
    this.loadModels();
  }

  private loadModels(): void {
    if (!this.woundId) {
      this.modelsLoadDone = true;
      this.checkAllLoaded();
      return;
    }

    this.woundService
      .getWoundModelNotesByWoundIds([this.woundId])
      .subscribe({
        next: (res) => {
          if (res.data) {
            const groupedByFile = res.data.reduce(
              (acc, item) => {
                const fileName = item.fileName;
                if (!acc[fileName]) {
                  acc[fileName] = { fileName, woundIds: [], allNotes: [] };
                }
                acc[fileName].woundIds.push(item.woundId);
                acc[fileName].allNotes = [
                  ...acc[fileName].allNotes,
                  ...item.notes,
                ];
                return acc;
              },
              {} as Record<string, any>,
            );

            this.models = Object.values(groupedByFile).map(
              (group: any, index: number) => {
                const url = group.fileName.toLowerCase();
                const is3D = url.endsWith('.gltf') || url.endsWith('.glb');
                const typeSuffix = is3D ? '3D Model' : '2D Model';
                const customName = `Scan ${index + 1} - ${typeSuffix}`;

                return {
                  name: customName,
                  url: group.fileName,
                  woundId: group.woundIds[0],
                  notes: group.allNotes,
                };
              },
            );

            if (this.models.length > 0) {
              const firstModelUrl = this.models[0].url;

              this.assessmentForm.patchValue(
                {
                  selectedModel1: firstModelUrl,
                },
                { emitEvent: true },
              );
              this.updateSelectedNotes();
            }
          }
          this.modelsLoadDone = true;
          this.checkAllLoaded();
        },
        error: () => {
          this.modelsLoadDone = true;
          this.checkAllLoaded();
        },
      });
  }

  private updateSelectedNotes(): void {
    const model1 = this.models.find((m) => m.url === this.selectedModel1);
    this.selectedModel1Notes = model1?.notes || [];
  }

  onModelChange(url: string): void {
    this.selectedModel1 = url;
    this.updateSelectedNotes();
  }

  // Update scores from latest assessment
  private updateScoresFromAssessments(): void {
    if (!this.wound?.woundAssessments?.length) return;

    this.latestAssessment = this.wound.woundAssessments[0];
    if (!this.latestAssessment?.woundAssessmentRiskCategories?.length) return;

    this.scores = {};
    this.latestAssessment.woundAssessmentRiskCategories.forEach((risk) => {
      const code = risk.assessmentTemplateCode?.toLowerCase();
      const value = risk.riskCategoryName
        ? `${risk.riskCategoryName} (${risk.totalScore})`
        : `${risk.totalScore}`;

      if (code) {
        this.scores[code] = value;
      }
    });
  }

  // Risk selection toggle

  // Tab management
  switchTab(tab: 'model' | 'documents' | 'analysis'): void {
    this.activeTab = tab;
  }

  private checkUserPermissions(): void {
    const session = this.currentUserService.GetSession();
    if (session && session.role) {
      const roleName = session.role.name?.toLowerCase();
      this.canUploadDocuments = roleName === 'nurse';
    }
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/patient', 'detail', this.patientId]);
  }
}
